import React, { useState } from "react";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { usePolling, usePrice, useNow } from "@/components/croc/hooks";
import { Panel, SectionTitle, Pill } from "@/components/croc/ui";
import { fmtNum, fmtUsd, pct, countdown } from "@/lib/format";
import api from "@/lib/api";
import { TrendingUp, TrendingDown, Timer, Zap } from "lucide-react";
import { PredictionSchedule } from "@/components/croc/PredictionSchedule";
import { ResolvedPredictions } from "@/components/croc/ResolvedPredictions";

export default function Prediction() {
  const { address } = useWallet();
  const price = usePrice();
  const { data, reload } = usePolling(() => api.predictionCurrent(address), [address], 5000);
  const { data: hist } = usePolling(() => api.predictionHistory(), [], 15000);
  const [amount, setAmount] = useState("10");
  const [busy, setBusy] = useState(false);
  useNow(1000);

  const round = data?.round;
  const locked = round ? Date.now() >= new Date(round.lock).getTime() : true;
  const cd = round ? countdown(locked ? round.close : round.lock) : { h: "00", m: "00", sec: "00" };

  const bet = async (side) => {
    const amt = parseFloat(amount) || 0;
    if (amt < 1) return toast.error("Minimum bet is $1");
    try {
      setBusy(true);
      await api.predictionBet({ address, side, amount: amt });
      toast.success(`${side.toUpperCase()} position placed`, { description: `${fmtUsd(amt)} on round #${round.round_no}` });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bet failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <SectionTitle
        eyebrow="CROC Bull/Bear · 5-Min Rounds"
        title="Prediction Market"
        subtitle="Predict the next close vs the entry price. Answer all daily predictions to earn a bonus equal to your daily staking reward, credited with the 00:00 GST payout."
        testid="prediction-title"
      />

      <PredictionSchedule />
      <ResolvedPredictions />

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel className="lg:col-span-2" glow="emerald" data-testid="prediction-round">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Pill tone="gold">Round #{round?.round_no ?? "—"}</Pill>
              <Pill tone={locked ? "danger" : "emerald"}>{locked ? "Locked" : "Open"}</Pill>
            </div>
            <div className="flex items-center gap-2 font-num text-white">
              <Timer className="w-4 h-4 gold-text" /> {cd.m}:{cd.sec}
            </div>
          </div>

          <div className="text-center py-6">
            <div className="text-xs text-muted-foreground">Live CROC Price</div>
            <div className="font-num font-black text-4xl text-white mt-1">${fmtNum(price?.price, 4)}</div>
            <div className="text-xs text-muted-foreground mt-1">Entry: ${fmtNum(round?.start_price, 4)}</div>
            <Pill tone="emerald" className="mt-3">Prediction reward = your daily staking reward</Pill>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl border border-[#10B981]/25 bg-[#10B981]/[0.05] p-4 text-center">
              <TrendingUp className="w-6 h-6 emerald-text mx-auto" />
              <div className="font-num font-bold text-xl text-white mt-2">{fmtNum(data?.up_payout, 2)}x</div>
              <div className="text-xs text-muted-foreground">UP pool {fmtUsd(round?.up_amount)}</div>
            </div>
            <div className="rounded-xl border border-red-500/25 bg-red-500/[0.05] p-4 text-center">
              <TrendingDown className="w-6 h-6 text-red-400 mx-auto" />
              <div className="font-num font-bold text-xl text-white mt-2">{fmtNum(data?.down_payout, 2)}x</div>
              <div className="text-xs text-muted-foreground">DOWN pool {fmtUsd(round?.down_amount)}</div>
            </div>
          </div>

          {data?.my_bet ? (
            <div className="mt-4 rounded-xl border border-[#1CFD4C]/30 bg-[#1CFD4C]/[0.06] px-4 py-3 text-center text-sm">
              You bet <span className="font-num gold-text">{fmtUsd(data.my_bet.amount)}</span> on{" "}
              <span className={data.my_bet.side === "up" ? "emerald-text" : "text-red-400"}>{data.my_bet.side.toUpperCase()}</span> this round.
            </div>
          ) : (
            <>
              <div className="mt-4 rounded-xl border border-white/10 bg-[#090E0B] p-3 flex items-center gap-3">
                <span className="text-xs text-muted-foreground">Bet amount</span>
                <input
                  data-testid="prediction-bet-amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="bg-transparent font-num font-bold text-white w-full outline-none text-right"
                />
                <span className="font-num text-white">USDT</span>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <button
                  data-testid="prediction-bull-bet-button"
                  disabled={locked || busy}
                  onClick={() => bet("up")}
                  className="flex items-center justify-center gap-2 rounded-xl bg-[#10B981] py-3.5 font-bold text-[#050806] hover:bg-[#34D399] transition-colors disabled:opacity-50"
                >
                  <TrendingUp className="w-4 h-4" /> Enter UP
                </button>
                <button
                  data-testid="prediction-bear-bet-button"
                  disabled={locked || busy}
                  onClick={() => bet("down")}
                  className="flex items-center justify-center gap-2 rounded-xl bg-red-500 py-3.5 font-bold text-white hover:bg-red-400 transition-colors disabled:opacity-50"
                >
                  <TrendingDown className="w-4 h-4" /> Enter DOWN
                </button>
              </div>
            </>
          )}
        </Panel>

        <Panel data-testid="prediction-history">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-4 h-4 gold-text" />
            <span className="font-display font-bold text-white">Recent Rounds</span>
          </div>
          <div className="space-y-2">
            {(hist?.rounds ?? []).map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-[#090E0B] px-3 py-2.5" data-testid={`round-history-${r.round_no}`}>
                <span className="font-num text-xs text-muted-foreground">#{r.round_no}</span>
                <span className="font-num text-xs text-white">${fmtNum(r.close_price, 4)}</span>
                {r.payout_total > 0 && (
                  <span className="font-num text-[11px] emerald-text" data-testid={`round-payout-${r.round_no}`} title={`${r.winners_count} winner(s) paid on-chain`}>
                    +{fmtUsd(r.payout_total)}
                  </span>
                )}
                <Pill tone={r.result === "up" ? "emerald" : "danger"}>{r.result === "up" ? "BULL" : "BEAR"}</Pill>
              </div>
            ))}
            {(hist?.rounds?.length ?? 0) === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No settled rounds yet.</div>}
          </div>
          <div className="mt-3 text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="prediction-onchain-note">
            <Zap className="w-3 h-3 gold-text" /> Winners are credited on-chain at settlement (PredictionMarket).
          </div>
        </Panel>
      </div>
    </div>
  );
}
